// Forest: instanced crossed-quad trees placed from the bake's crown packs
// (crowns/<i>_<j>.cpk, CPK1: crownpack.js decodeCrowns; vegtiles.js: the
// draw range around the beam target / the player, then by distance). One
// InstancedMesh per species per 1024 m chunk (= pack tile) -> few draw calls,
// frustum culled. Textures are procedural species atlases (no assets),
// alpha-tested so they work with fog and depth without sorting.
//
// SPECIES ARE PIPELINE DATA (rev 49): the bake stores a per-tree canopy
// APPEARANCE byte sampled from the pinned ortho (dark stand vs light stand),
// and meta.vegetation (region.json, authored by the AI region/vegetation
// profiles) says which REAL species those stands are — 8 built-in forms
// (conifer/larch/broadleaf/palm/columnar/spreading/umbrella/birch) plus
// AI-generated custom forms via meta.vegetation.forms. The client maps
// appearance -> species by
// a share-weighted brightness LUT, so light larch stands in the photo render
// as larch and dark stone-pine stands as stone pine. A tree without an ortho
// reading (appearance 255: operator include stands, no ortho under it) takes
// the elevation-ramp species split. A bake whose trees lack the appearance
// bytes / appear_hist, or whose meta.vegetation names no species, throws; so
// does a tree-bearing bake without the crown pack (meta.crowns CPK1).
//
// A CROWN HAS NO Y (APP_FORESTVOL §2a): it stands on the DRAWN ground — the
// ground tree's finest drawn node (terrain.groundDrawn), sampled when its
// chunk builds and again whenever the eye has halved the distance it was
// sampled from (the drawn node refines with it) and whenever a finer node is
// drawn under its read (yfrom.js, FT1: a read off a coarse level is off the
// ground by up to its tolerance); a crown whose ground is not drawn yet is
// left out and its chunk built again. Every per-tree choice
// (silhouette variant, rotation, the species draw of an unread crown) keys on
// the crown's position hash (crownHash), never on a load-order index.
//
// Shader work (all MeshBasic + onBeforeCompile, zero lights): per-instance
// silhouette variant (aVar -> atlas column), tip-weighted wind sway, and
// fwidth-sharpened alpha test so needle fringes stay crisp instead of eroding
// through the mip chain at distance.
//
// MEASURED vegetation (rev >= 66, layout ..._rad_u8): bakes made from a
// provider DSM carry the real per-crown height + crown radius (surface_ndsm);
// _hgt/_wf consume them directly and the krummholz ramp / per-form width
// become the statistical-scatter fallback. The measured shrub band renders in
// shrubs.js.
//
// HAND-OFF (gs-sprite-handoff, handoff.js; APP_FORESTVOL §4b): every crown is
// drawn, full density, out to the sprite range N; over the band [N − F, N]
// the sprites screen-door out with the dither the far canopy volume
// (forestvol.js) screen-doors in — at every pixel exactly one of the two.
// N = min(live gensurf sprites.view_m, the cap radius: the largest radius
// whose crowns fit renderconfig.trees.render_cap, so the drawn instances
// stay bounded); the volume takes over past it in both passes (no wedge
// trees). Where a canopy tile is resident under a sprite fragment, the
// sprite takes the tile's light + snow (B: sky-vis, sun-vis, snow-catch),
// ramped in over the 2F before N: near and far agree through the band.
//
// ZOOM CROWNS (zoom-crowns; renderconfig focus.tiers.<t>.trees, desktop):
// while the focus wedge is active, every crown inside it past the naked N is
// ONE apex-facing quad on the focus layer (WedgeScatter, the same atlas /
// species / season through twin materials that share every uniform), out to
// the optic range No = min(the distance at which a ref_h_m crown is still
// min_px tall in the optic target at the zoom, range_m, the wedge's cap
// radius) — nearest first under `cap`. During an optic pass the hand-off's N
// is No (handoff.js setHandoffPass via focus.setPass), so the volume steps
// back to No with the same dither; uSplit hands each crown to exactly one of
// near sprite / zoom quad by its trunk distance. The naked pass is unchanged.
import * as THREE from 'three';
import RENDER_CFG from './renderconfig.json';
import { detailTier } from './terrain.js';
import { deviceClass, classDraw } from './device.js';
import { classBlock } from './focus.js';
// the sprite materials, atlases, quads, hero extras + the instance transform:
// vegmat.js (shared with the dashboard's mini forest, vegforest.js)
import {
  patchSpriteMat, makeTreeGeoParts, buildSpriteGeometry, poolAtlas, atlasSprite, disposeAtlas, setSpritePage, setSpriteSeason,
  heroTrunkMaterial, makeHeroTrunkGeometry, makeHeroFanGeometry, treeMatrix, treeVar, vegSpriteUrl,
} from './vegmat.js';
import { fetchAsset } from './assetq.js';
import { VegTiles } from './vegtiles.js';
import { readCrownHeader } from './crownpack.js';
import { run as poolRun } from './pool.js';
import { COLL_M } from './pool-veg.js';
import { slice } from './sched.js';
import {
  FORM_CFG, TREE_ALPHA, resolveSpecies, speciesLum, speciesLut,
} from './vegpaint.js';
import {
  HANDOFF, HANDOFF_GLSL, HO_TREE_GLSL, HO_PAD_M, setSpriteCap, capRadius, nakedNear, setOpticNear, opticRange,
  zoomPlan, zoomLo, ZOOM_SLACK_M,
} from './handoff.js';
import { forestVolLookup, forestVolGround, CANOPY_LOOKUP_GLSL, SNOW_COL_GLSL } from './forestvol.js';
import { vrsTreeChunk, vrsNoHero, vrsTreeR } from './vrstyle/trees.js';
import { CANOPY_N, spriteSeason } from './crowns.js';
import { SEASON, applySeason, readSea, seaRamp } from './season.js';
import { genLive, onGenChange } from './gensched.js';
import { Y_NONE, YFrom, yStale, yMoved } from './yfrom.js';

const TCFG = RENDER_CFG.trees || { view_m: 1100, render_cap: 30000 };
const FOCUS_LAYER = (RENDER_CFG.focus && RENDER_CFG.focus.layer) || 5;
const VIEW_M = TCFG.view_m;             // the ceiling of the live sprite range: the tiles streamed
const HERO_R = TCFG.hero_r || 60;       // near-field sprite-fan trees inside this radius
const HERO_CAP = TCFG.hero_cap || 320;  // counted in validate.py's tri gate
const STREAM_M = 64;                    // re-ask the tiles around the player every STREAM_M
const BUILD_PAD_M = 256;                // a chunk holds every crown within N + this of its build position
const CAP_M = 32;                       // the cap radius is measured again every CAP_M of movement
const CAP_BIN_M = 8;
const CAP_Q_M = 128;                     // the build radius follows the cap radius in CAP_Q_M steps
const BUILD_MS = 2;                     // a chunk build's slice a frame
const RETRY_Y_MS = 1000;                // a chunk with undrawn ground under a crown is built again after (once the ground tree moved: _groundGen)
// a chunk's LOD signature: the 192 m near-cell of its build position
const chunkSig = (pos) => Math.round(pos.x / 192) + ',' + Math.round(pos.z / 192);
const _nowMs = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
const _AT = { k: Y_NONE };               // groundDrawn's level out (_yAt)
let BANDS_TREELINE = NaN;     // meta.bands.treeline_m (setTreeline, main.js boot)

export function setTreeline(bands) {
  if (!bands || !Number.isFinite(bands.treeline_m)) throw new Error('world meta.bands.treeline_m missing: rebuild this world (Build)');
  BANDS_TREELINE = bands.treeline_m;
}

// the live sprite range (gensurf sprites.view_m; no gensurf block: the ceiling)
const liveView = () => { const l = genLive(); return (l && l.sprites && l.sprites.view_m) || VIEW_M; };
// the class's sprite radius: the live view × CLASS_DRAW tree_view (device.js;
// quest 0.6 = 660 m, every other class the view itself) — held as a cap
// (_cap), so the hand-off's N = min(view, cap) and the canopy volume is the
// forest beyond, exactly as past the density cap today (quest-merge)
export const treeViewM = (cls) => liveView() * classDraw('tree_view', cls);

// FORM_CFG (8 built-in forms), NVAR and every painter live in vegpaint.js —
// shared with the dashboard preview.
const _DCFG = FORM_CFG.conifer;

// THE TRUNK-COLLISION GRID (COLL_M cells): per committed tile its cells as
// CSR (pool-veg.js trees.tile), in commit order. get(cx, cz) = every tile's
// slots of that cell, tile after tile in commit order, each in record order
// — what the Map of 'cx_cz' string keys it replaced held (slots pushed tile
// by tile as the tiles landed); null for an empty cell.
export class CollGrid {
  constructor() { this.tiles = []; }
  add(g) { if (g && g.w && g.h) this.tiles.push(g); }
  get(cx, cz) {
    let out = null, many = false;
    for (const g of this.tiles) {
      const u = cx - g.x0, v = cz - g.z0;
      if (u < 0 || v < 0 || u >= g.w || v >= g.h) continue;
      const c = v * g.w + u, a = g.off[c], b = g.off[c + 1];
      if (a === b) continue;
      const s = g.items.subarray(a, b);
      if (!out) { out = s; continue; }
      if (!many) { out = Array.from(out); many = true; }
      for (let k = 0; k < s.length; k++) out.push(s[k]);
    }
    return out;
  }
}

export class Forest {
  // terrain: the TerrainManager (groundDrawn: the drawn ground a crown stands on);
  // sea: the world's season data (SEA1, season.js; main.js boot) — required
  constructor(scene, meta, sea, baseUrl, terrain = null) {
    this.scene = scene;
    this.ground = terrain;
    forestVolGround(terrain);   // the far volume's wall feet seat on the same drawn ground (forestvol THE SEAT)
    this.chunks = new Map();   // "cx_cz" -> Group (drawn)
    this.queue = new Set();    // "cx_cz" due a (re)build: ONE job at a time (_buildNext), the old group draws until then
    this.job = null;           // {k, it}: the chunk build in progress (sliced)
    this.trees = null;
    this.byChunk = new Map();  // "cx_cz" -> crown slots (a chunk IS a pack tile: chunkM = tile_m)
    this.tileBox = new Map();  // a tile's crown slots -> its crowns' [x0, x1, z0, z1]
    this.collGrid = new CollGrid(); // COLL_M cell -> crown slots (trunk collision)
    this.run = poolRun;        // the client worker pool (pool.js): the atlases, the tiles' index
    this._tileQ = Promise.resolve();   // the tiles' index jobs, committed in landing order (_tile)
    this._tilePend = 0;        // tiles landed, index not committed yet
    this._atlasWait = false;   // start() .. the species atlases landed (nearSettled holds)
    this.lastUpdate = new THREE.Vector3(1e9, 0, 0);
    this.lastCap = new THREE.Vector3(1e9, 0, 0);
    this.capR = Infinity;
    this.veg = (meta && meta.vegetation) || null;      // region.json via bake meta
    this.sea = readSea(sea);                            // the world's season: snow band, species ramps (season.js SEA1)
    this.time = { value: 0 };                           // wind clock (uTime)
    this._layout = (meta && meta.trees && meta.trees.layout) || '';
    this._dataGen = 0;                                  // bumps when the arrays / species change
    this.baseUrl = baseUrl;                             // the owner's sprites: veg/<sha>.webp
    this._spriteGen = 0;
    // the crown packs (vegtiles.js): the world-wide arrays are allocated here
    // and filled pack by pack (slots start..start+n of each), so a loaded
    // crown never moves
    const n0 = (meta.trees && meta.trees.count) || 0;
    const L = this._layout;
    this.measured = L.includes('rad');
    this._hist = (meta.trees && meta.trees.appear_hist) || null;
    if (n0) {
      if (!meta.crowns || meta.crowns.layout !== 'CPK1') throw new Error('world meta.crowns (CPK1 crown pack) missing: rebuild this world (Build)');
      if (!L.includes('appear')) throw new Error(`world meta.trees.layout ${L || '(none)'} has no appearance byte: rebuild this world (Build)`);
      if (!Array.isArray(this._hist)) throw new Error('world meta.trees.appear_hist missing: rebuild this world (Build)');
      if (!this.veg || !Array.isArray(this.veg.species) || !this.veg.species.length) {
        throw new Error('world meta.vegetation.species missing: the forest has no species to draw — rebuild this world (Build)');
      }
      // one crown: x, z + h (scale byte) + appearance + r (0.2 m) + its hash key
      this.tiles = new VegTiles(meta.crowns, baseUrl, 6, (t, ab) => this._tile(t, ab));
      const n = this.tiles.count;
      this.chunkM = meta.crowns.tile_m;
      this.xyz = new Float32Array(n * 3);   // y: the drawn ground, NaN until sampled
      this.yd = new Float32Array(n);        // the eye distance y was sampled from
      this.yk = new Uint8Array(n);          // the drawn level it was read on (Y_NONE: none yet)
      this.scales = new Uint8Array(n);
      this.appear = new Uint8Array(n);
      this.radius = new Uint8Array(n);
      this.key = new Uint32Array(n);        // crownHash >>> 12: every per-tree choice
    } else {
      this.tiles = null;
      this.chunkM = 1024;
    }
    this._at = new THREE.Vector3(1e9, 0, 0);            // where / when stream() last asked
    this._atT = 0;
    if (this.tiles) globalThis.__trees = this;          // the dev probe hook (zoom set, crown y)
    onGenChange((kinds) => {
      if (kinds.has('uniform')) { this.lastUpdate.set(1e9, 0, 0); this.lastCap.set(1e9, 0, 0); }
      if (kinds.has('uniform') || kinds.has('regen')) this._applySeason();
    });
  }

  // main.js starts the forest in its load phase 1 (materials, species, hero
  // meshes; no data); the tiles come through stream() / retarget(). The bake
  // already dropped every tree on a way, a building, under a deck / canopy,
  // in a bore or a lift corridor (bake.py VegCover, t39): the client filters
  // nothing. Resolves when the set-up has settled (main.js: the late shader
  // warm-up).
  start() {
    if (!this.tiles || this._started) return Promise.resolve();
    this._started = true;
    this._atlasWait = true;
    return this._setup().catch(() => {});
  }

  // the tiles around pos: the draw range as 'near' (the beam-in latch waits
  // for it, nearSettled), one tile further as 'bulk'; every STREAM_M of
  // movement or 2 s (a failed tile is asked again after RETRY_MS), or now
  // (force: a new target before entry, main.js loadRetarget)
  stream(pos, force = false) {
    if (!this.tiles) return;
    const now = performance.now();
    if (!force && Math.hypot(pos.x - this._at.x, pos.z - this._at.z) < STREAM_M && now - this._atT < 2000) return;
    this._at.set(pos.x, 0, pos.z);
    this._atT = now;
    this.tiles.want(pos.x, pos.z, VIEW_M, 'near');
    this.tiles.want(pos.x, pos.z, VIEW_M + this.chunkM, 'bulk');
  }

  // every tile of the draw range around pos landed (or failed: the latch's
  // timeout is the backstop for a tile that never comes) and its index is
  // committed (_tile), and the species atlases landed (_setupSpecies: a
  // species draws only then)
  nearSettled(pos) {
    return !this.tiles || (!this._tilePend && !this._atlasWait && this.tiles.settled(pos.x, pos.z, VIEW_M));
  }

  // the Map's view [x0, z0, x1, z1] (maplayers.js paintTrees): its tiles, bulk
  wantRect(rect) { if (this.tiles) this.tiles.wantRect(rect); }

  // one pack landed (VegTiles): its crowns fill slots start..start+n. Its
  // header is checked here (a foreign pack throws: the tile fails); the
  // decode + the index (pool-veg.js trees.tile: x, z, the hash key, h = the
  // scale byte (measured h·8, the scatter h·10), r = r / 0.2 m (0 =
  // unmeasured stand), appear = the ortho brightness (255 = none), the
  // chunk's slots, the tile's trunk-collision cells) are built in the pool
  // and committed in the order the tiles landed (_tileIn) — the arrays,
  // byChunk and collGrid as the in-place build gave them. A malformed body
  // fails in the pool: logged, the tile's slots stay out.
  _tile(t, ab) {
    const hd = readCrownHeader(ab);
    if (hd.kind !== 0 || hd.i !== t.i || hd.j !== t.j || hd.n !== t.n) throw new Error(`pack holds ${hd.kind} ${hd.i}_${hd.j} n ${hd.n}`);
    const job = this.run('trees.tile', { ab, i: t.i, j: t.j, n: t.n, start: t.start },
      { transfer: [ab], prio: t.prio === 'near' ? 'near' : 'bulk' });
    this._tilePend++;
    this._tileQ = this._tileQ.then(() => job)
      .then((r) => this._tileIn(t, r), (e) => console.error(`[veg] crowns ${t.i}_${t.j}: ${e.message}`))
      .finally(() => { this._tilePend--; });
  }

  // a tile's index (trees.tile) into the world-wide arrays: chunk index
  // (render) + COLL_M trunk-collision cells, committed together so the drawn
  // forest and the solid trunks can never disagree
  _tileIn(t, r) {
    const s = t.start;
    this.xyz.set(r.xyz, s * 3);
    this.yd.fill(Infinity, s, s + t.n);
    this.yk.fill(Y_NONE, s, s + t.n);
    this.key.set(r.key, s);
    this.scales.set(r.h, s);
    this.appear.set(r.appear, s);
    this.radius.set(r.r, s);
    this.collGrid.add(r.grid);
    this.byChunk.set(t.i + '_' + t.j, r.ids);
    // the tile's crown xz box (_chunkJob skips a tile wholly past its radius)
    let bx0 = Infinity, bx1 = -Infinity, bz0 = Infinity, bz1 = -Infinity;
    for (let k = 0; k < t.n; k++) {
      const x = r.xyz[k * 3], z = r.xyz[k * 3 + 2];
      if (x < bx0) bx0 = x;
      if (x > bx1) bx1 = x;
      if (z < bz0) bz0 = z;
      if (z > bz1) bz1 = z;
    }
    this.tileBox.set(r.ids, [bx0, bx1, bz0, bz1]);
    this.lastUpdate.set(1e9, 0, 0);
    this.lastCap.set(1e9, 0, 0);
    if (this.lastHero) this.lastHero.set(1e9, 0, 0);
    this._dataGen++;
  }

  // crown i's ground y seen from distance d: sampled again once the eye has
  // halved the distance it was sampled from or a finer node is drawn under
  // its read (yfrom.js yStale); NaN while no node is drawn there (no ground
  // host: the stored y). from (a YFrom, optional): the set's coarse reads
  _yAt(i, d, from = null) {
    if (!this.ground || !this.yd) return this.xyz[i * 3 + 1];
    const x = this.xyz[i * 3], z = this.xyz[i * 3 + 2];
    if (d < this.yd[i] * 0.5 || !Number.isFinite(this.xyz[i * 3 + 1]) || yStale(this.ground, x, z, this.yk[i])) {
      const y = this.ground.groundDrawn(x, z, _AT);
      if (Number.isFinite(y)) { this.xyz[i * 3 + 1] = y; this.yd[i] = Math.max(d, 1); this.yk[i] = _AT.k; }
    }
    if (from) from.add(this.yk[i], x, z);
    return this.xyz[i * 3 + 1];
  }

  // the per-tree key (position hash; fixtures without packs: the slot)
  _key(i) { return this.key ? this.key[i] : i; }

  async _setup() {
    await slice();
    this.geoParts = makeTreeGeoParts();
    this._setupSpecies();
    // hero trees (rev 81): within HERO_R every tree gains a second pair of
    // crossed quads at 45°/135° — interleaved with the chunk sprite's 0°/90°
    // pair it makes an 8-plane star, so the flat-billboard tell disappears
    // without any opaque shell hiding the form-true atlas painting (the old
    // cone/blob shells read as dark paper triangles and buried the airy larch
    // silhouette exactly where the player looks). Same atlas materials, so
    // wind, alpha test and silhouette variant match the sprite exactly. Plus
    // a low-poly 3D trunk filling the edge-on gap at the base. Rebuilt every
    // ~18 m; the fan meshes themselves are (re)built by _setupSpecies.
    this.heroTrunk = new THREE.InstancedMesh(makeHeroTrunkGeometry(), heroTrunkMaterial(), HERO_CAP);
    this.heroTrunk.count = 0;
    this.heroTrunk.frustumCulled = false;
    this.heroTrunk.matrixAutoUpdate = false;   // static: the instances carry the placement
    this.scene.add(this.heroTrunk);
    this.lastHero = new THREE.Vector3(1e9, 0, 0);
    this.lastUpdate.set(1e9, 0, 0);
    this.ready = true;
  }

  // build the species slots from the bake's meta.vegetation (region.json —
  // never a global tunable: a tunables key is shared by every world and
  // leaked one world's species into all of them): one sprite atlas material
  // each, plus the appearance->species brightness LUT and the elevation ramp
  // for the trees without an ortho reading
  _setupSpecies() {
    const veg = this.veg;
    const vegForms = veg.forms || {};
    this.vegForms = vegForms;
    // ≤ 4 slots, unknown form → conifer, bad colour → form default, shares
    // renormalised — resolveSpecies (vegpaint.js), the one the preview uses
    const sp = resolveSpecies(veg);
    this.species = sp;
    // the world's vegetation treeline (region.vegetation.treeline_m, via the
    // bake meta) beats the bands treeline
    this.treeline = Number(veg.treeline_m) || BANDS_TREELINE;
    // sprite atlas resolution follows the device tier (same sniff as terrain)
    const S = [128, 192, 256][detailTier()] || 192;
    if (this.mats) this.mats.forEach((m) => m.dispose());
    if (this.atlases) this.atlases.forEach(disposeAtlas);
    // the canopy lookup (forestvol.js; none without a volume: no tile anywhere)
    this.look = forestVolLookup();
    // the atlases are painted in the pool (vegmat.js poolAtlas); a species'
    // material draws once its atlas landed (visible), its zoom twin with it
    const atlases = this.atlases = sp.map((s) => poolAtlas(this.run, 'species', { form: s.form, color: s.color, S, forms: vegForms }));
    const mats = this.mats = sp.map((s, i) => {
      const m = new THREE.MeshBasicMaterial({
        map: atlases[i].tex,
        alphaTest: TREE_ALPHA, side: THREE.DoubleSide, fog: true,
      });
      m.visible = false;
      patchSpriteMat(m, this.time);
      patchHandoff(m, this.look);
      setSpritePage(m, atlases[i], TREE_ALPHA).catch((err) => console.warn('[season page]', err));   // async: the page lands when derived
      return m;
    });
    this._setupZoom();
    this._atlasWait = true;
    Promise.all(atlases.map((a, i) => a.ready.then(() => {
      if (this.mats !== mats) return;
      mats[i].visible = true;
      if (this.zmats && this.zmats[i]) this.zmats[i].visible = true;
    }))).catch((err) => console.warn('[veg atlas]', err))   // (a failed paint: loud; the wait ends either way)
      .then(() => { if (this.mats === mats) this._atlasWait = false; });
    this._applySeason();
    this._loadSprites(veg.species || []);
    this._buildHeroSprites();
    this._dataGen++;
    // the elevation ramp's endpoints (_species): darkest vs lightest canopy color
    const lum = sp.map((s) => speciesLum(s.color));
    this._lum = lum;
    this.darkIdx = lum.indexOf(Math.min(...lum));
    this.lightIdx = lum.indexOf(Math.max(...lum));
    this.pLightBase = sp[this.lightIdx] ? sp[this.lightIdx].share : 0.22;
    this._buildLut();
  }

  // the owner's sprite (species[i].sprite = sha16, shipped as WebP; resolveSpecies keeps the
  // order, so i is the slot): fetched as bulk and drawn into every variant
  // cell of that slot's atlas — the painter stands until it lands, and stays
  // when the bake does not carry the file. Chunk and hero meshes share
  // the material, so one texture update restyles them all.
  _loadSprites(list) {
    const gen = ++this._spriteGen;
    const mats = this.mats, atlases = this.atlases;
    list.slice(0, mats.length).forEach((s, i) => {
      const sha = s && s.sprite;
      if (!this.baseUrl || typeof sha !== 'string' || !/^[0-9a-f]{16}$/.test(sha)) return;
      fetchAsset(vegSpriteUrl(this.baseUrl, sha), { prio: 'bulk', as: 'blob' })
        .then(async (b) => {
          if (!b || gen !== this._spriteGen || this.mats !== mats) return;
          // into every cell, in the pool (vegmat.js atlasSprite: the texture re-uploads)
          if (!(await atlasSprite(atlases[i], b, 'tree')) || gen !== this._spriteGen || this.mats !== mats) return;
          await setSpritePage(mats[i], atlases[i], TREE_ALPHA);   // the owner's picture's own page (async)
          if (gen !== this._spriteGen || this.mats !== mats) return;
          mats[i].userData.vegOwn.value.set(1, 1);   // every column: the owner look, once its page is bound
        })
        .catch(() => {});
    });
  }

  // R8h2 world swap (worldswap.js vegHook): the new meta.vegetation, whose
  // sprite references alone moved — the set-up reads it from here on; set up
  // already, the sprites load again (_loadSprites: its _spriteGen drops the
  // old ones' late answers; the composite clears the slot, as at boot)
  reloadSprites(veg) {
    this.veg = veg || null;
    if (this.mats && this.veg) this._loadSprites(this.veg.species || []);
  }

  // the season on the sprites (gs-sprite-snow, APP_FORESTVOL §5): THE world
  // uSeason (season.js applySeason — forestvol applies the same live block)
  // + per species its tint / bare / catch at that day (crowns.js
  // spriteSeason; a species' own ramp — SEA1, seaRamp — beats the form's). On setup
  // and on every live change (season.date regen, the rest uniforms); before
  // the tunables land: the default no-snow state at REF_DOY.
  _applySeason() {
    if (!this.mats) return;
    const live = genLive();
    if (live && live.season) {
      try { applySeason(live.season, this.sea); } catch (e) { console.error('[trees] season:', e.message); }
    }
    const named = (this.veg && this.veg.species) || [];
    this.species.forEach((s, i) => {
      if (this.mats[i]) setSpriteSeason(this.mats[i], spriteSeason(s.form, SEASON.doy, seaRamp(this.sea, named[i] && named[i].name)));
    });
  }

  // (re)build the per-species hero fan meshes — one InstancedMesh per
  // species sharing that species' atlas material, so the extra planes are
  // pixel-identical to the chunk sprite (same painting, wind and alpha test)
  _buildHeroSprites() {
    if (this.heroSprites) {
      for (const m of this.heroSprites) {
        this.scene.remove(m);
        m.geometry.dispose();
        m.dispose();
      }
    }
    this.heroSprites = this.species.map((_, sp) => {
      const m = new THREE.InstancedMesh(makeHeroFanGeometry(HERO_CAP), this.mats[sp], HERO_CAP);
      m.count = 0;
      m.frustumCulled = false;
      m.matrixAutoUpdate = false;   // static: the instances carry the placement
      m.onBeforeRender = this._sync;
      this.scene.add(m);
      return m;
    });
  }

  // per sprite draw: the canopy textures / indirection in step (forestvol)
  _sync = () => { if (this.look) this.look.sync(); };

  // the zoom crowns' materials: one twin per species material (same atlas,
  // HO_ZOOM: the trunk partition's far side) sharing its season page, season,
  // catch and owner-look uniforms, so every restyle of a species reaches both;
  // + a hidden keeper so warm-up compiles the zoom program before the first
  // zoom. Only on a focus tier whose trees block is live for this device
  // class (focus.js classBlock).
  _setupZoom() {
    const blk = RENDER_CFG.focus && RENDER_CFG.focus.tiers && RENDER_CFG.focus.tiers[detailTier()];
    if (!blk || !classBlock(blk.trees, deviceClass())) return;
    if (this.zmats) this.zmats.forEach((m) => m.dispose());
    this.zmats = this.mats.map((b) => {
      const m = new THREE.MeshBasicMaterial({ map: b.map, alphaTest: TREE_ALPHA, side: THREE.DoubleSide, fog: true });
      m.visible = b.visible;                     // drawn once the atlas landed (_setupSpecies)
      patchSpriteMat(m, this.time);
      Object.assign(m.userData.sprSeason, b.userData.sprSeason);
      m.userData.vegOwn = b.userData.vegOwn;
      patchHandoff(m, this.look, true);
      return m;
    });
    if (!this.zoom) {
      this.zoom = new WedgeScatter(this.scene, FOCUS_LAYER, makeWedgeQuadGeometry, (st) => this._zoomCommit(st));
    }
    this.zoom.reset(this.zmats);
    this._zoomKey = null;
    if (this.zoomKeeper) { this.scene.remove(this.zoomKeeper); this.zoomKeeper.geometry.dispose(); this.zoomKeeper.dispose(); }
    const k = new THREE.InstancedMesh(makeWedgeQuadGeometry(1), this.zmats[0], 1);
    k.name = 'zoom-crowns-keeper';
    k.visible = false;
    k.frustumCulled = false;
    k.matrixAutoUpdate = false;
    k.layers.set(FOCUS_LAYER);
    this.scene.add(k);
    this.zoomKeeper = k;
  }

  // appearance byte (canopy brightness percentile in THIS bake) -> species,
  // share-weighted: the darkest fraction of stands gets the darkest species.
  // The histogram is the bake's world-wide one (meta.trees.appear_hist), so a
  // tree maps to the same species whichever tiles are loaded.
  _buildLut() {
    this.lut = null;
    if (!this.species) return;
    this.lut = speciesLut(Uint32Array.from(this._hist), this.species);
  }

  // per-chunk geometries carry their own aVar instance attribute, so they
  // must be disposed with the mesh or their GPU buffers leak
  _disposeChunk(g) {
    this.scene.remove(g);
    g.traverse((o) => {
      if (o.isInstancedMesh) { o.geometry.dispose(); o.dispose(); }
    });
  }

  // drawn/collidable height of tree i. Measured bakes (nDSM crowns) carry
  // the REAL height — including the real krummholz shrinking — so the ramp
  // below is only for the statistical scatter (and a measured bake's
  // unmeasured include stands).
  _hgt(i) {
    // the two tree sources (pipeline/bake.py bake_trees): measured
    // (surface_ndsm) encodes height x8 (ceiling 31.9 m), the osm_scatter x10
    const s = this.scales[i] / (this.measured ? 8 : 10);
    if (this.measured && this.radius[i]) return s;
    const y = this.xyz[i * 3 + 1];
    const k = 0.45 + 0.55 * Math.min(1, Math.max(0, (this.treeline - y) / 140));
    return s * k;
  }

  // sprite/hero width factor (fraction of height) for tree i: the measured
  // crown radius when the bake has one, else the per-form default. The atlas
  // silhouette spans ~0.85 of its quad, hence the 2.35 (= 2/0.85) diameter
  // fit; clamped so extreme aspect ratios stay tree-shaped.
  _wf(i, s, form) {
    if (this.measured && this.radius[i]) {
      const r = this.radius[i] * 0.2;
      return Math.min(1.15, Math.max(0.28, (2.35 * r) / Math.max(1, s)));
    }
    return (FORM_CFG[form] || _DCFG).w;
  }

  // solid trunks: push a position (player capsule radius pr) out of any
  // trunk circle. Radius follows the hero-trunk visual (0.035 x height);
  // vertical band = trunk+lower canopy, so gliding OVER a tree stays free.
  resolveTrunks(pos, pr = 0.35) {
    if (!this.collGrid) return false;
    const cx = Math.floor(pos.x / COLL_M), cz = Math.floor(pos.z / COLL_M);
    let hit = false;
    for (let dz = -1; dz <= 1; dz++) {
      for (let dx = -1; dx <= 1; dx++) {
        const idx = this.collGrid.get(cx + dx, cz + dz);
        if (!idx) continue;
        for (const i of idx) {
          const tx = this.xyz[i * 3], tz = this.xyz[i * 3 + 2];
          const ty = this._yAt(i, Math.hypot(pos.x - tx, pos.z - tz));
          if (!Number.isFinite(ty)) continue;  // no drawn ground under it yet
          const s = this._hgt(i);              // drawn height m (krummholz-scaled)
          if (pos.y < ty - 1.2 || pos.y > ty + s * 0.8) continue;
          const r = Math.min(0.55, Math.max(0.25, s * 0.035)) + pr;
          const dxp = pos.x - tx, dzp = pos.z - tz;
          const d2 = dxp * dxp + dzp * dzp;
          if (d2 >= r * r || d2 < 1e-8) continue;
          const d = Math.sqrt(d2);
          pos.x = tx + (dxp / d) * r;
          pos.z = tz + (dzp / d) * r;
          hit = true;
        }
      }
    }
    return hit;
  }

  // focus (optional): the zoom crowns in the focus wedge
  update(pos, focus = null) {
    this.stream(pos);
    if (!this.ready) return;
    this.time.value = performance.now() / 1000;   // wind clock
    if (!vrsNoHero() && (pos.distanceTo(this.lastHero) >= 18 || yMoved(this._heroY, this.ground, _nowMs(), RETRY_Y_MS))) this._updateHero(pos);
    if (pos.distanceTo(this.lastCap) >= CAP_M) this._cap(pos);
    if (pos.distanceTo(this.lastUpdate) >= 96) this._plan(pos);
    this._retryY();
    this._buildNext(pos);
    this._updateZoom(pos, focus);
  }

  // ------------------------------------------------------------ zoom crowns
  // One set per (wedge gen, zoom step, data, naked N, ZOOM_SLACK_M step of the
  // eye, a retry for crowns whose ground was not drawn): a sliced job, the
  // old set draws until the new one commits, and the set's No reaches the
  // hand-off AT the commit (_zoomCommit) — the volume steps back exactly as
  // far as the drawn set reaches. No block / no wedge: nothing, No 0.
  _updateZoom(pos, focus) {
    const blk = this.zoom && focus && focus.active ? focus.trees : null;
    if (!blk) {
      if (this.zoom) this.zoom.hide();
      if (this.zoomNo) { this.zoomNo = 0; setOpticNear(0); }
      this._zoomKey = null;
      return;
    }
    if (!this._zoomAt || Math.hypot(pos.x - this._zoomAt.x, pos.z - this._zoomAt.z) > ZOOM_SLACK_M
      || (this._zoomRetry && _nowMs() >= this._zoomRetry && this._groundGen() !== this._zoomMissGen)
      || yMoved(this._zoomY, this.ground, _nowMs(), RETRY_Y_MS)) {
      this._zoomAt = pos.clone();
      this._zoomRetry = 0;
      this._zoomStep = (this._zoomStep || 0) + 1;
    }
    const naked = nakedNear();
    const key = `${focus.gen}:${focus.zoomGen}:${this._dataGen}:${Math.round(naked)}:${this._zoomStep}`;
    if (key !== this._zoomKey) {
      this._zoomKey = key;
      this.tiles.wantWedge(focus, blk.range_m);   // the wedge's packs, bulk by distance
    }
    const at = this._zoomAt;
    this.zoom.step(key, () => this._zoomJob(at.x, at.z, focus, blk, naked));
  }

  _zoomCommit(stage) {
    for (const m of this.zoom.meshes) if (m) m.onBeforeRender = this._sync;   // the canopy lookup in step
    this.zoomNo = stage.no;
    this.zoomStats = { n: stage.reduce((a, st) => a + st.n, 0), no: stage.no, at: stage.at };
    setOpticNear(stage.no);
  }

  // The candidates: crowns inside the wedge (chunk disc test with the REAL
  // Focus.hit, then the per-crown sector test from the apex) whose distance
  // from the eye (px, pz) lies in (zoomLo(naked), the optic range +
  // HO_PAD_M]; handoff.js zoomPlan gives No (the optic range at the zoom
  // step's fov, the cap radius, the slack) and lim: the set is every
  // candidate within lim — nearest first, ≤ cap — so every crown the shader
  // can reach (uSplit < trunk ≤ No + HO_PAD_M from an eye within
  // ZOOM_SLACK_M of (px, pz)) is in it. y: the drawn ground.
  * _zoomJob(px, pz, focus, blk, naked) {
    const gen0 = this._groundGen();   // the ground the job reads (a retry waits for it to move)
    const ox = focus.ox, oz = focus.oz, ax = focus.ax, az = focus.az;
    const cosH = Math.cos(Math.min(Math.PI, focus.halfRad));
    const range = opticRange(focus.rangeFov, focus.rtPx, blk.min_px, blk.ref_h_m, blk.range_m);
    const lo = zoomLo(naked), R = range + HO_PAD_M;
    const C = this.chunkM, hd = C * 0.7072;
    const cand = [], dist = [];
    let tick = 0;
    const x0 = Math.floor((ox - R) / C), x1 = Math.floor((ox + R) / C);
    const z0 = Math.floor((oz - R) / C), z1 = Math.floor((oz + R) / C);
    for (let cz = z0; cz <= z1; cz++) {
      for (let cx = x0; cx <= x1; cx++) {
        const idx = this.byChunk.get(cx + '_' + cz);
        if (!idx || !focus.hit((cx + 0.5) * C, (cz + 0.5) * C, hd, R)) continue;
        for (const i of idx) {
          if (++tick >= 2048) { tick = 0; yield; }
          const x = this.xyz[i * 3], z = this.xyz[i * 3 + 2];
          const d = Math.hypot(x - px, z - pz);
          if (d <= lo || d > R) continue;
          const dx = x - ox, dz = z - oz, da = Math.hypot(dx, dz);
          if (dx * ax + dz * az < da * cosH) continue;            // outside the sector
          cand.push(i); dist.push(d);
        }
      }
    }
    const plan = zoomPlan(dist, dist.length, naked, range, blk.cap);
    const nSp = this.species.length;
    const stage = Array.from({ length: nSp }, () => ({ n: 0, m: null, v: null }));
    stage.no = plan.no;
    stage.at = [px, pz];
    if (!stage.no) return stage;
    const lim = plan.lim;
    const sel = [], spOf = [], from = new YFrom();
    const counts = new Int32Array(nSp);
    let missing = 0;
    for (let k = 0; k < cand.length; k++) {
      if (++tick >= 2048) { tick = 0; yield; }
      if (dist[k] > lim) continue;
      const i = cand[k];
      if (!Number.isFinite(this._yAt(i, dist[k], from))) { missing++; continue; }
      const sp = this._species(i);
      sel.push(i); spOf.push(sp); counts[sp]++;
    }
    if (missing) { this._zoomRetry = _nowMs() + RETRY_Y_MS; this._zoomMissGen = gen0; }
    this._zoomY = { from, gen: gen0, at: _nowMs() };
    for (let sp = 0; sp < nSp; sp++) { stage[sp].m = new Float32Array(counts[sp] * 16); stage[sp].v = new Float32Array(counts[sp]); }
    for (let k = 0; k < sel.length; k++) {
      if (++tick >= 2048) { tick = 0; yield; }
      const i = sel[k], sp = spOf[k], st = stage[sp];
      const form = this.species[sp].form;
      const s = this._hgt(i) * (FORM_CFG[form] || _DCFG).hScale;
      const w = s * this._wf(i, s, form);
      writeFacing(st.m, st.n * 16, this.xyz[i * 3], this.xyz[i * 3 + 1] - 0.3, this.xyz[i * 3 + 2], ox, oz, w, s);
      st.v[st.n++] = treeVar(this._key(i));
    }
    return stage;
  }

  // the cap radius: the largest r whose crowns (trunk within r + HO_PAD_M —
  // the sprites drawn at all) fit render_cap; the hand-off's N = min(live
  // view, r) (handoff.js setSpriteCap), so the volume takes over nearer
  // where the forest is dense
  _cap(pos) {
    this.lastCap.copy(pos);
    // only crowns within the class's radius can move the answer (quest-move
    // 2026-11-02): past Rv (treeViewM + HO_PAD_M up to a whole bin) a first
    // over-cap bin gives r >= treeViewM, which the min below clips to it
    // anyway, so the histogram stops there — the same cap; R = the live view
    // + pad wherever tree_view is 1 (every class but the quest)
    const V = treeViewM();
    const Rv = Math.ceil((V + HO_PAD_M) / CAP_BIN_M) * CAP_BIN_M;
    const R = Math.min(liveView() + HO_PAD_M, Rv), nb = Math.ceil(R / CAP_BIN_M) + 1;
    const hist = new Uint32Array(nb);
    let total = 0;
    const C = this.chunkM;
    for (let cz = Math.floor((pos.z - R) / C); cz <= Math.floor((pos.z + R) / C); cz++) {
      for (let cx = Math.floor((pos.x - R) / C); cx <= Math.floor((pos.x + R) / C); cx++) {
        const idx = this.byChunk.get(cx + '_' + cz);
        if (!idx) continue;
        for (const i of idx) {
          const d = Math.hypot(this.xyz[i * 3] - pos.x, this.xyz[i * 3 + 2] - pos.z);
          if (d > R) continue;
          hist[Math.ceil(d / CAP_BIN_M)]++;
          total++;
        }
      }
    }
    const cap = Math.min(capRadius(hist, CAP_BIN_M, TCFG.render_cap, total), treeViewM());   // the class's radius caps it too (quest-merge)
    const R0 = this._buildR();
    this.capR = cap;
    setSpriteCap(cap);
    if (this._buildR() !== R0) this.lastUpdate.set(1e9, 0, 0);   // the build radius moved: plan again
  }

  // every 96 m (or after a tile landed / a live change): the chunks due a
  // (re)build — QUEUED, never built here: a sig change used to rebuild every
  // chunk in one frame (up to 9 chunks x 4 species, 17-26 ms; the moving
  // hitch). Chunks out of range go at once.
  _plan(pos) {
    this.lastUpdate.copy(pos);
    const R = this._buildR();
    const c0x = Math.floor((pos.x - R) / this.cellM);
    const c1x = Math.floor((pos.x + R) / this.cellM);
    const c0z = Math.floor((pos.z - R) / this.cellM);
    const c1z = Math.floor((pos.z + R) / this.cellM);
    const want = new Set();
    // a chunk built elsewhere holds the crowns within N + BUILD_PAD_M of ITS
    // build position, and the ys sampled from there: key the build on the
    // signature (near-cell of the build position)
    const sig = chunkSig(pos);
    for (let cz = c0z; cz <= c1z; cz++) {
      for (let cx = c0x; cx <= c1x; cx++) {
        const k = cx + '_' + cz;
        const nt = this._cellTiles(k).length;
        if (!nt) continue;
        want.add(k);
        const cur = this.chunks.get(k);
        // a tile landed into a built cell (nt grew): built again
        if (!cur || cur.userData.sig !== sig || cur.userData.R !== R || cur.userData.nt !== nt) this.queue.add(k);
      }
    }
    for (const [k, g] of this.chunks) {
      if (!want.has(k)) {
        this._disposeChunk(g);
        this.chunks.delete(k);
      }
    }
    for (const k of this.queue) if (!want.has(k)) this.queue.delete(k);
    if (this.job && !want.has(this.job.k)) this.job = null;
  }

  // a chunk holds the crowns within min(live view, the cap radius in
  // CAP_Q_M steps) + BUILD_PAD_M: dense forest submits fewer collapsed ones
  _buildR() {
    return vrsTreeR(Math.min(liveView(), Math.ceil(this.capR / CAP_Q_M) * CAP_Q_M) + BUILD_PAD_M);
  }

  // a chunk left a crown out (no drawn ground under it yet): built again once
  // the ground tree moved (pbc-forest 2026-10-24: a crown over a node never
  // drawn rebuilt its chunk / the zoom set every RETRY_Y_MS forever); a chunk
  // whose crowns read a coarse node that a finer one replaced (yfrom.js
  // yMoved, FT1): built again, and the hero fan with it
  _retryY() {
    const now = _nowMs(), gen = this._groundGen();
    for (const [k, g] of this.chunks) {
      if (this.queue.has(k)) continue;
      if (g.userData.missing && now - g.userData.at >= RETRY_Y_MS && g.userData.gen !== gen) { g.userData.at = now; this.queue.add(k); }
      else if (yMoved(g.userData.y, this.ground, now, RETRY_Y_MS)) { this.queue.add(k); if (this.lastHero) this.lastHero.set(1e9, 0, 0); }
    }
  }

  // the ground tree's generation (TerrainManager.gen: a node shown / evicted
  // — the only change groundDrawn sees); 0 without a ground host
  _groundGen() { return this.ground && Number.isFinite(this.ground.gen) ? this.ground.gen : 0; }

  // ONE chunk job at a time — a missing chunk first (it draws nothing yet),
  // then the nearest — sliced at BUILD_MS a frame, built at the position it
  // started from and swapped in whole
  _buildNext(pos) {
    if (!this.job) {
      if (!this.queue.size) return null;
      let best = null, bd = Infinity;
      for (const k of this.queue) {
        const [cx, cz] = k.split('_').map(Number);
        const d = Math.hypot(pos.x - (cx + 0.5) * this.cellM, pos.z - (cz + 0.5) * this.cellM)
          - (this.chunks.has(k) ? 0 : 1e7);
        if (d < bd) { bd = d; best = k; }
      }
      this.queue.delete(best);
      this.job = { k: best, it: this._chunkJob(best, pos.clone(), chunkSig(pos)) };
    }
    const end = _nowMs() + BUILD_MS;
    let r;
    do { r = this.job.it.next(); } while (!r.done && _nowMs() < end);
    if (!r.done) return null;
    const k = this.job.k;
    this.job = null;
    return k;
  }

  // the whole build at once (fixtures)
  _buildChunk(k, pos, sig) {
    const it = this._chunkJob(k, pos, sig);
    while (!it.next().done);
  }

  // rebuild the near-field hero set: every baked tree within HERO_R gets the
  // extra fan planes + a 3D trunk (full density — the near ring is the whole
  // point). Transform, rotation and aVar are IDENTICAL to the chunk sprite's,
  // so the fan planes interleave the crossed pair at exact 45° spacing.
  _updateHero(pos) {
    this.lastHero.copy(pos);
    const c0x = Math.floor((pos.x - HERO_R) / this.chunkM);
    const c1x = Math.floor((pos.x + HERO_R) / this.chunkM);
    const c0z = Math.floor((pos.z - HERO_R) / this.chunkM);
    const c1z = Math.floor((pos.z + HERO_R) / this.chunkM);
    const M = new THREE.Matrix4();
    const counts = this.heroSprites.map(() => 0);
    const from = new YFrom();
    let n = 0;
    for (let cz = c0z; cz <= c1z && n < HERO_CAP; cz++) {
      for (let cx = c0x; cx <= c1x && n < HERO_CAP; cx++) {
        const idx = this.byChunk.get(cx + '_' + cz);
        if (!idx) continue;
        for (const i of idx) {
          if (n >= HERO_CAP) break;
          const d = Math.hypot(this.xyz[i * 3] - pos.x, this.xyz[i * 3 + 2] - pos.z);
          if (d > HERO_R) continue;
          const y = this._yAt(i, d, from);
          if (!Number.isFinite(y)) continue;
          const sp = this._species(i);
          const form = this.species[sp].form;
          let s = this._hgt(i);
          s *= (FORM_CFG[form] || _DCFG).hScale;
          const wf = this._wf(i, s, form);
          const key = this._key(i);
          treeMatrix(M, this.xyz[i * 3], y, this.xyz[i * 3 + 2], key, s, wf);
          this.heroTrunk.setMatrixAt(n, M);
          const fan = this.heroSprites[sp];
          const c = counts[sp];
          fan.setMatrixAt(c, M);
          fan.geometry.getAttribute('aVar').setX(c, treeVar(key));
          counts[sp] = c + 1;
          n++;
        }
      }
    }
    this._heroY = { from, gen: this._groundGen(), at: _nowMs() };
    this.heroTrunk.count = n;
    this.heroTrunk.instanceMatrix.needsUpdate = true;
    for (let sp = 0; sp < this.heroSprites.length; sp++) {
      const fan = this.heroSprites[sp];
      fan.count = counts[sp];
      fan.instanceMatrix.needsUpdate = true;
      fan.geometry.getAttribute('aVar').needsUpdate = true;
    }
  }

  _species(i) {
    // per-tree appearance from the bake (canopy brightness in the real ortho)
    // mapped onto the region's species (no LUT: not one tree has a reading)
    if (this.lut) {
      const a = this.appear[i];
      if (a !== 255) return this.lut[a];
    }
    // no ortho reading (255): light-species share grows toward the treeline
    const y = this.xyz[i * 3 + 1];
    const ramp = Math.min(1, Math.max(0, (y - (this.treeline - 350)) / 550));
    const p = Math.min(0.85, this.pLightBase + ramp * 0.45);
    return ((this._key(i) * 2654435761 >>> 8) % 1000) / 1000 < p ? this.lightIdx : this.darkIdx;
  }

  // chunk k built at pos: every crown within the live sprite range +
  // BUILD_PAD_M of it whose drawn ground is in (the hand-off's fragment rule
  // draws exactly the ones nearer than N; a farther trunk's vertices
  // collapse); yields every 2048 crowns, swaps the group in at the end
  * _chunkJob(k, pos, sig) {
    const tiles = this._cellTiles(k);
    const R = this._buildR(), gen0 = this._groundGen();
    const idx = [], from = new YFrom();
    let missing = 0, tick = 0;
    for (const all of tiles) {
      // a tile whose every crown lies past R adds none (quest-move
      // 2026-11-02: a 2-tile quest cell, and the build radius well inside it);
      // no box (a tile never seen by _tileIn) = the full test below
      const bb = this.tileBox && this.tileBox.get(all);
      if (bb) {
        const ex = Math.max(bb[0] - pos.x, 0, pos.x - bb[1]), ez = Math.max(bb[2] - pos.z, 0, pos.z - bb[3]);
        if (Math.hypot(ex, ez) > R) continue;
      }
      for (const i of all) {
        if (++tick >= 2048) { tick = 0; yield; }
        const d = Math.hypot(this.xyz[i * 3] - pos.x, this.xyz[i * 3 + 2] - pos.z);
        if (d > R) continue;
        if (!Number.isFinite(this._yAt(i, d, from))) { missing++; continue; }
        idx.push(i);
      }
    }
    const group = new THREE.Group();
    group.userData.sig = sig;
    group.userData.R = R;
    group.userData.nt = tiles.length;
    group.userData.missing = missing;
    group.userData.gen = gen0;
    group.userData.at = _nowMs();
    group.userData.y = { from, gen: gen0, at: group.userData.at };   // its coarse reads (_retryY)
    group.matrixAutoUpdate = false;   // static: the instances carry the placement
    const M = new THREE.Matrix4();
    const spOf = new Uint8Array(idx.length);
    const counts = new Int32Array(this.species.length);
    for (let q = 0; q < idx.length; q++) { const sp = this._species(idx[q]); spOf[q] = sp; counts[sp]++; }
    for (let sp = 0; sp < this.species.length; sp++) {
      if (!counts[sp]) continue;
      const form = this.species[sp].form;
      // per-chunk geometry: shares the quad arrays, carries its own aVar
      // instance attribute (the atlas column per tree)
      const geo = buildSpriteGeometry(this.geoParts);
      const av = new Float32Array(counts[sp]);
      const m = new THREE.InstancedMesh(geo, this.mats[sp], counts[sp]);
      let wMax = 0;                       // widest instance xz scale (sway pad)
      let n = 0;
      for (let q = 0; q < idx.length; q++) {
        if (spOf[q] !== sp) continue;
        if (++tick >= 2048) { tick = 0; yield; }
        const i = idx[q];
        let s = this._hgt(i);             // height m (measured, or ramped)
        s *= (FORM_CFG[form] || _DCFG).hScale;
        const wf = this._wf(i, s, form);  // measured crown width when baked
        if (s * wf > wMax) wMax = s * wf;
        const key = this._key(i);
        treeMatrix(M, this.xyz[i * 3], this.xyz[i * 3 + 1], this.xyz[i * 3 + 2], key, s, wf);
        m.setMatrixAt(n, M);
        av[n++] = treeVar(key);
      }
      geo.setAttribute('aVar', new THREE.InstancedBufferAttribute(av, 1));
      m.instanceMatrix.needsUpdate = true;
      // frustum-cull the chunk per camera (main, scope/mono RT, XR). The
      // bound must hold every vertex patchSpriteMat can move: the only vertex
      // displacement is the wind sway, local xz += (sin, cos) * y² * 0.025
      // with y <= 1 on the unit quad, i.e. |Δ| <= 0.025·√2 local, applied
      // BEFORE the instance matrix, so it scales by the instance's xz scale
      // s·wf (not its height) -> world pad 0.025·√2·max(s·wf) (≤ 1.3 m at
      // the 31.9 m height ceiling × wf 1.15). The hand-off collapse only
      // shrinks. Fresh bounds per build (every rebuild makes a new mesh).
      m.computeBoundingSphere();
      m.boundingSphere.radius += Math.max(3, 0.025 * Math.SQRT2 * wMax);
      m.frustumCulled = true;
      m.matrixAutoUpdate = false;
      m.onBeforeRender = this._sync;
      group.add(m);
    }
    vrsTreeChunk(this, group);   // THE VR LOOK: solid crowns (vrstyle/trees.js)
    this.scene.add(group);
    group.updateMatrixWorld(true);
    const old = this.chunks.get(k);   // drew until now: replaced in the same frame, never a gap
    if (old) this._disposeChunk(old);
    this.chunks.set(k, group);
  }

  // the DRAW unit (quest-merge 2026-11-01): a cell of CLASS_DRAW tree_cell
  // tiles a side (device.js, read at use — the class is decided before the
  // forest exists; quest 2 = 2048 m: one instanced mesh a species per cell,
  // the same instances, a coarser frustum cull), every other class the tile
  // itself. byChunk stays per tile; chunks / queue key cells.
  get cellT() { return Math.max(1, Math.round(classDraw('tree_cell'))); }
  get cellM() { return this.chunkM * this.cellT; }

  // the crown slot lists of the pack tiles in draw cell k ("cx_cz" in cells;
  // cellT a side; the tile itself at cellT 1) — the ones landed so far
  _cellTiles(k) {
    if (this.cellT === 1) { const t = this.byChunk.get(k); return t ? [t] : []; }
    const [cx, cz] = k.split('_').map(Number), out = [];
    for (let i = 0; i < this.cellT; i++) {
      for (let j = 0; j < this.cellT; j++) {
        const t = this.byChunk.get((cx * this.cellT + i) + '_' + (cz * this.cellT + j));
        if (t) out.push(t);
      }
    }
    return out;
  }
}

// The hand-off on a tree sprite material (handoff.js): the VS collapses a
// sprite whose trunk lies past uSplit (N + HO_PAD_M but in an optic pass
// with a zoom set: the naked N) — zoom = a zoom crowns' twin (HO_ZOOM): it
// keeps exactly the trunks uSplit < t ≤ uNear + HO_PAD_M — and hands the fragment its world
// position; the FS keeps the sprite's pixels of the band (hoSpriteKeep) and,
// over a resident canopy tile (look: forestvol's lookup; null = none
// anywhere), takes the tile's light + snow ramped in by hoWeight — the
// sprite's own page snow (vegmat.js patchSpriteMat, which declares uSeason +
// snowWeight) ramps out by the same weight (SPR_SNOW_K), so the band never
// snows twice. The look rides the program key (a world with a volume
// compiles the canopy variant).
function patchHandoff(mat, look, zoom = false) {
  const base = mat.onBeforeCompile, baseKey = mat.customProgramCacheKey();
  mat.onBeforeCompile = (shader, renderer) => {
    base(shader, renderer);
    shader.uniforms.uNear = HANDOFF.uNear;
    shader.uniforms.uFade = HANDOFF.uFade;
    shader.uniforms.uSplit = HANDOFF.uSplit;
    if (look) Object.assign(shader.uniforms, look.uniforms);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>\nuniform float uNear, uSplit;\nvarying vec3 vHoW;\nvarying vec2 vHoTree;\n${HO_TREE_GLSL}`)
      .replace('#include <project_vertex>', `#include <project_vertex>
        {
          vec4 hoP = vec4(transformed, 1.0);
        #ifdef USE_INSTANCING
          hoP = instanceMatrix * hoP;
          vec3 hoT = (modelMatrix * instanceMatrix[3]).xyz;
          float hoTd = length(hoT.xz - cameraPosition.xz);
        #ifdef HO_ZOOM
          if (hoTd <= uSplit || hoTd > uNear + ${HO_PAD_M.toFixed(1)}) gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
        #else
          if (hoTd > uSplit) gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
        #endif
        #endif
          vHoW = (modelMatrix * hoP).xyz;
        #ifdef USE_INSTANCING
          vHoTree = vec2(hoTd, hoTreeThr(hoT.xz));   // the TREE's hand-off: its trunk distance + threshold
        #else
          vHoTree = vec2(length(vHoW.xz - cameraPosition.xz), 0.5);
        #endif
        }`)
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>
uniform float uNear, uFade;
varying vec3 vHoW;
varying vec2 vHoTree;
${HANDOFF_GLSL}
#ifdef HO_CANOPY
#define FV_N ${CANOPY_N}
uniform highp sampler2DArray uB;
uniform vec3 uSky, uSun, uL;
uniform float uWhite, uSnowGain;
${CANOPY_LOOKUP_GLSL}${SNOW_COL_GLSL}
#define SPR_SNOW_K (1.0 - hoWeight(hoD, hoTile))
#endif`)
      .replace('#include <clipping_planes_fragment>', `#include <clipping_planes_fragment>
#ifdef HO_CANOPY
  ivec4 hoT; float hoY0;
  bool hoTile = tileOf(ivec2(floor(vHoW.xz / FV_IND)), hoT, hoY0);
#else
  bool hoTile = false;
#endif
  float hoD = length(vHoW.xz - cameraPosition.xz);
  if (!hoSpriteKeep(vHoTree.x, vHoTree.y, hoTile)) discard;   // whole trees (handoff.js): no per-pixel dots`)
      .replace('#include <tonemapping_fragment>', `#ifdef HO_CANOPY
  if (hoTile) {
    vec2 hoUV = (vHoW.xz - vec2(hoT.zw) * FV_IND) / (texelM(hoT.y) * float(FV_N));
    vec4 hoB = textureLod(uB, vec3(clamp(hoUV, 0.0, 1.0), float(hoT.x)), 0.0);
    float hoW = hoWeight(hoD, true);
    gl_FragColor.rgb *= hoLight(hoB.rg, uSky, uSun, uL.y, hoW);
    vec3 hoE = hoB.r * uSky + uSun * (max(uL.y, 0.0) * hoB.g);
    gl_FragColor.rgb = mix(gl_FragColor.rgb, snowCol(hoE), snowWeight(vHoW.y, 0.0, 0.0, hoB.b) * hoW);
  }
#endif
#include <tonemapping_fragment>`);
  };
  if (look) mat.defines = { ...(mat.defines || {}), HO_CANOPY: '' };
  if (zoom) mat.defines = { ...(mat.defines || {}), HO_ZOOM: '' };
  mat.customProgramCacheKey = () => baseKey + (look ? ':ho1' : ':ho0') + (zoom ? 'z' : '');
}

// ------------------------------------------------------------- wedge scatter
// Far vegetation in the scope wedge (shrubs.js; the trees' zoom crowns,
// Forest._updateZoom): one
// preallocated-per-slot InstancedMesh of ONE quad (2 tris) per material slot,
// on the focus layer only, frustumCulled off (the wedge IS the cull). The set
// is built by a host generator (`job`): it yields every ~2048 items and
// returns per-slot staging {n, m: Float32Array(16 n) matrices, v: aVar}; step()
// runs it for at most WEDGE_BUDGET_MS per frame and commits it whole, so the
// old set keeps drawing until the new one is complete. Slot meshes grow (never
// shrink) to the largest set seen, in 4096-instance steps. onCommit(stage):
// called right after a set went in (the zoom crowns hand their No over).
const WEDGE_BUDGET_MS = 2;

export class WedgeScatter {
  constructor(scene, layer, makeGeo, onCommit = null) {
    this.scene = scene;
    this.layer = layer;
    this.makeGeo = makeGeo;       // (capacity) -> BufferGeometry with an aVar instance attribute
    this.onCommit = onCommit;
    this.mats = [];
    this.meshes = [];
    this.key = null;
    this.job = null;
  }

  // new material set (species restyle): drop the meshes, rebuild on next commit
  reset(mats) {
    for (const m of this.meshes) if (m) { this.scene.remove(m); m.geometry.dispose(); m.dispose(); }
    this.mats = mats;
    this.meshes = mats.map(() => null);
    this.key = null;
    this.job = null;
  }

  hide() {
    if (this.key === null) return;
    for (const m of this.meshes) if (m) m.count = 0;
    this.key = null;
    this.job = null;
  }

  step(key, start) {
    if (key !== this.key) { this.key = key; this.job = start(); }
    if (!this.job) return;
    const end = _nowMs() + WEDGE_BUDGET_MS;
    let r;
    do { r = this.job.next(); } while (!r.done && _nowMs() < end);
    if (!r.done) return;
    this.job = null;
    this._commit(r.value);
    if (this.onCommit) this.onCommit(r.value);
  }

  _commit(stage) {
    for (let sl = 0; sl < this.mats.length; sl++) {
      const st = stage[sl];
      const n = st ? st.n : 0;
      let m = this.meshes[sl];
      if (!n) { if (m) m.count = 0; continue; }
      if (!m || m.instanceMatrix.count < n) {
        if (m) { this.scene.remove(m); m.geometry.dispose(); m.dispose(); }
        const cap = Math.ceil(n / 4096) * 4096;
        m = new THREE.InstancedMesh(this.makeGeo(cap), this.mats[sl], cap);
        m.frustumCulled = false;
        m.matrixAutoUpdate = false;   // static: the instances carry the placement
        m.layers.set(this.layer);
        m.name = 'wedge-veg';
        this.scene.add(m);
        this.meshes[sl] = m;
      }
      const im = m.instanceMatrix, av = m.geometry.getAttribute('aVar');
      im.array.set(st.m.subarray(0, n * 16));
      av.array.set(st.v.subarray(0, n));
      im.clearUpdateRanges(); im.addUpdateRange(0, n * 16); im.needsUpdate = true;
      av.clearUpdateRanges(); av.addUpdateRange(0, n); av.needsUpdate = true;
      m.count = n;
    }
  }
}

// Growable (index, score) list with a deterministic top-k: a 1024-bin
// histogram of log(score) finds the threshold, the boundary bin is filled in
// insertion order. top() is a generator (yields every 8192 items) — run it
// with `yield*` inside a WedgeScatter job, so a big set never stalls a frame.
export class Candidates {
  constructor() { this.n = 0; this.i = new Int32Array(65536); this.s = new Float32Array(65536); }
  push(i, s) {
    if (this.n === this.i.length) {
      const ni = new Int32Array(this.n * 2); ni.set(this.i); this.i = ni;
      const ns = new Float32Array(this.n * 2); ns.set(this.s); this.s = ns;
    }
    this.i[this.n] = i; this.s[this.n] = Math.log(s); this.n++;
  }
  * top(cap) {
    const n = this.n, ls = this.s;
    if (n <= cap) return this.i.subarray(0, n);
    let lo = Infinity, hi = -Infinity;
    for (let k = 0; k < n; k++) {
      const v = ls[k]; if (v < lo) lo = v; if (v > hi) hi = v;
      if ((k & 8191) === 8191) yield;
    }
    const B = 1024, sc = (B - 1) / Math.max(1e-9, hi - lo);
    const bin = new Uint16Array(n), hist = new Uint32Array(B);
    for (let k = 0; k < n; k++) {
      const b = Math.min(B - 1, Math.floor((ls[k] - lo) * sc)); bin[k] = b; hist[b]++;
      if ((k & 8191) === 8191) yield;
    }
    let b = B - 1, above = 0;
    while (b >= 0 && above + hist[b] <= cap) { above += hist[b]; b--; }
    let room = cap - above;                 // items taken from the boundary bin b
    const out = new Int32Array(cap);
    let o = 0;
    for (let k = 0; k < n; k++) {
      if (bin[k] > b) out[o++] = this.i[k];
      else if (bin[k] === b && room > 0) { out[o++] = this.i[k]; room--; }
      if ((k & 8191) === 8191) yield;
    }
    return out.subarray(0, o);
  }
}

// instance matrix of a unit quad (local x across, y up, normal +z) standing at
// (x, y, z), turned about Y so its face looks at the apex (ox, oz), scaled
// w x h — written straight into a column-major Float32Array at offset o
export function writeFacing(a, o, x, y, z, ox, oz, w, h) {
  let fx = ox - x, fz = oz - z;
  const l = Math.hypot(fx, fz) || 1;
  fx /= l; fz /= l;                        // = (sin yaw, cos yaw): local +z -> apex
  a[o] = fz * w; a[o + 1] = 0; a[o + 2] = -fx * w; a[o + 3] = 0;
  a[o + 4] = 0; a[o + 5] = h; a[o + 6] = 0; a[o + 7] = 0;
  a[o + 8] = fx; a[o + 9] = 0; a[o + 10] = fz; a[o + 11] = 0;
  a[o + 12] = x; a[o + 13] = y; a[o + 14] = z; a[o + 15] = 1;
}

// ONE vertical unit quad, pivot at the base, facing +z, with its own aVar
// instance attribute (atlas column) sized to the mesh capacity
export function makeWedgeQuadGeometry(cap) {
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(
    [-0.5, 0, 0, 0.5, 0, 0, 0.5, 1, 0, -0.5, 1, 0], 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute([0, 0, 1, 0, 1, 1, 0, 1], 2));
  geo.setIndex([0, 1, 2, 0, 2, 3]);
  geo.setAttribute('aVar', new THREE.InstancedBufferAttribute(new Float32Array(cap), 1));
  return geo;
}

// Corridor test for Grass.exclude: true within r m of any of the polylines
// (arrays of [x,y,z]). Points are densified and bucketed into r-sized cells,
// so a query is one 3x3 lookup. Used for the funicular's open-air runs. The
// trees' and shrubs' lift corridors are the bake's (bake.py _CorridorHit,
// the same test, t39).
// numeric cell keys (quest-move 2026-11-02: the grass exclude asks per cold
// cell; a string key per neighbour was the mask's whole cost); |cell| < 2^20
const maskKey = (cx, cz) => (cx + 1048576) * 2097152 + (cz + 1048576);
export function corridorMask(paths, r = 7) {
  const grid = new Map();
  for (const p of paths || []) {
    const pts = (p && p.points) || [];
    for (let i = 1; i < pts.length; i++) {
      const x0 = pts[i - 1][0], z0 = pts[i - 1][2];
      const x1 = pts[i][0], z1 = pts[i][2];
      const n = Math.max(1, Math.ceil(Math.hypot(x1 - x0, z1 - z0) / (r * 0.5)));
      for (let k = 0; k <= n; k++) {
        const x = x0 + (x1 - x0) * (k / n), z = z0 + (z1 - z0) * (k / n);
        const key = maskKey(Math.floor(x / r), Math.floor(z / r));
        let a = grid.get(key);
        if (!a) grid.set(key, a = []);
        a.push(x, z);
      }
    }
  }
  const r2 = r * r;
  return (x, z) => {
    const cx = Math.floor(x / r), cz = Math.floor(z / r);
    for (let dz = -1; dz <= 1; dz++) {
      for (let dx = -1; dx <= 1; dx++) {
        const a = grid.get(maskKey(cx + dx, cz + dz));
        if (!a) continue;
        for (let i = 0; i < a.length; i += 2) {
          const ex = a[i] - x, ez = a[i + 1] - z;
          if (ex * ex + ez * ez < r2) return true;
        }
      }
    }
    return false;
  };
}

